/**
 * Bottom promo bar.
 *
 * Dismissal is remembered per message: the storage key carries a hash of the
 * rendered text, so editing the message brings the bar back for everyone who
 * had closed the previous one.
 *
 * The bar is fixed, so it would otherwise sit on top of the last slice of every
 * page. It publishes its measured height as `--promo-bar-height` on the root
 * element; the section's stylesheet uses that to reserve matching space at the
 * bottom of the page and to lift the sticky add-to-cart bar clear of it.
 */
const HEIGHT_PROPERTY = '--promo-bar-height';
const MS_PER_DAY = 86400000;

class PromoBar extends HTMLElement {
  #resizeObserver = null;

  connectedCallback() {
    this.querySelector('[data-promo-bar-close]')?.addEventListener('click', this.#dismiss);

    this.#resizeObserver = new ResizeObserver(() => this.#publishHeight());
    this.#resizeObserver.observe(this);
    this.#publishHeight();
  }

  disconnectedCallback() {
    this.querySelector('[data-promo-bar-close]')?.removeEventListener('click', this.#dismiss);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#setHeight(0);
  }

  #dismiss = () => {
    const days = Number(this.dataset.dismissDays);
    const key = this.dataset.storageKey;

    if (key && days > 0) {
      try {
        localStorage.setItem(key, String(Date.now() + days * MS_PER_DAY));
      } catch {
        // Private browsing or a full quota — the bar still closes for this page view.
      }
    }

    this.hidden = true;
    this.#setHeight(0);
  };

  #publishHeight() {
    this.#setHeight(this.hidden ? 0 : this.offsetHeight);
  }

  /** @param {number} pixels */
  #setHeight(pixels) {
    document.documentElement.style.setProperty(HEIGHT_PROPERTY, `${pixels}px`);
  }
}

if (!customElements.get('promo-bar')) {
  customElements.define('promo-bar', PromoBar);
}
